/* Show a Judge.me review only on the locale it was written in.  (Ales 2026-09-15)
 *
 * A Croatian review was rendering on the English page. Ales: the original language is right -
 * reviews are never rewritten - "but needs to be in croatian only when croatian language is
 * showing".
 *
 * Why this is done in the browser and not in Liquid or CSS:
 *   - Judge.me injects the reviews with JavaScript after the page loads, so Liquid never sees them.
 *   - Judge.me DOES emit a per-review attribute, data-review-language, but measured on the live
 *     widget (2026-09-15) its value is EMPTY. The widget does not tell us the language, so there is
 *     nothing for a CSS attribute selector to match. The language has to be worked out from the
 *     review text itself.
 *
 * The detector is deliberately simple and conservative:
 *   - It scores four languages on WORD markers, not just characters. The review that prompted this
 *     is Croatian typed WITHOUT diacritics ("narudzba", "odusevljena", "receno"), so anything
 *     keyed on š/č/ž alone would have missed it.
 *   - hr and sl are close, so they are separated on the words that genuinely differ
 *     (hr: je, nije, ovo, vrlo, hvala / sl: je, ni, to, zelo, hvala + the sl-only ones below).
 *   - WHEN IN DOUBT IT SHOWS THE REVIEW. A wrongly hidden review is worse than a wrongly shown
 *     one: hiding loses a real customer's words and makes the shop look emptier than it is.
 *     So a review is hidden ONLY when the detector is confident AND the language differs.
 *
 * The score in the summary header is left alone on purpose: a 5-star review is 5 stars whatever
 * language it was written in, so the average must keep counting every review.
 */
(function () {
  'use strict';

  /* Word markers. Short function words are the reliable signal in a two-sentence review;
     content words are too product-specific to generalise. */
  var MARK = {
    en: ['the', 'and', 'this', 'with', 'for', 'very', 'was', 'have', 'from', 'that', 'skin',
         'product', 'after', 'using', 'really', 'love', 'great', 'good', 'first', 'order'],
    de: ['und', 'der', 'die', 'das', 'ist', 'ich', 'nicht', 'sehr', 'mit', 'ein', 'eine', 'auch',
         'haut', 'sich', 'aber', 'schon', 'wie', 'bei', 'habe', 'mich', 'fuer', 'ueber'],
    hr: ['je', 'ovo', 'sam', 'nije', 'vrlo', 'ali', 'kao', 'sto', 'sve', 'jako', 'jos', 'vec',
         'bez', 'koja', 'koji', 'prva', 'prvi', 'kraju', 'proizvod', 'narudzba', 'narudžba',
         'kozu', 'kožu', 'hvala', 'puno', 'preporucujem', 'preporučujem', 'brzina', 'cijene',
         'cijena', 'odusevljena', 'oduševljena', 'predivni', 'pakiranje'],
    sl: ['je', 'to', 'sem', 'ni', 'zelo', 'pa', 'kot', 'kar', 'vse', 'se', 'bom', 'bi', 'lahko',
         'kozo', 'kožo', 'izdelek', 'narocilo', 'naročilo', 'hvala', 'priporocam', 'priporočam',
         'dostava', 'cena', 'zadovoljna', 'super', 'res']
  };

  /* Words that appear in ONE of the close pair and effectively never in the other. These break
     the hr/sl tie, which plain marker counting cannot do reliably. */
  var ONLY_HR = ['ovo', 'sto', 'nije', 'vrlo', 'jos', 'vec', 'kao sto', 'narudzba', 'narudžba',
                 'pakiranje', 'cijena', 'cijene', 'preporucujem', 'preporučujem', 'kozu', 'kožu'];
  var ONLY_SL = ['zelo', 'sem', 'naročilo', 'narocilo', 'priporocam', 'priporočam', 'izdelek',
                 'kozo', 'kožo', 'cena', 'lahko', 'kar'];

  function detect(text) {
    if (!text) return null;
    var t = ' ' + text.toLowerCase().replace(/[^\p{L}\s]/gu, ' ').replace(/\s+/g, ' ') + ' ';
    var words = t.trim().split(' ').filter(Boolean);
    if (words.length < 4) return null;               // too short to judge - show it

    var score = {};
    Object.keys(MARK).forEach(function (lang) {
      var n = 0;
      MARK[lang].forEach(function (w) { if (t.indexOf(' ' + w + ' ') !== -1) n++; });
      score[lang] = n;
    });

    /* German is also signalled by its capitalised nouns and umlauts; English by the absence of
       every Slavic marker. These nudges only ever add to an already-counted score. */
    if (/[äöüß]/.test(text)) score.de += 2;
    if (/[čćžšđ]/.test(text)) { score.hr += 1; score.sl += 1; }

    ONLY_HR.forEach(function (w) { if (t.indexOf(' ' + w + ' ') !== -1) score.hr += 2; });
    ONLY_SL.forEach(function (w) { if (t.indexOf(' ' + w + ' ') !== -1) score.sl += 2; });

    var ranked = Object.keys(score).sort(function (a, b) { return score[b] - score[a]; });
    var top = ranked[0], second = ranked[1];

    /* Confidence gate. Both conditions must hold, otherwise we return null and the review stays
       visible everywhere - the safe direction. */
    if (score[top] < 2) return null;                       // nothing really matched
    if (score[top] - score[second] < 2) return null;       // too close to call

    return top;
  }

  function pageLang() {
    var l = (document.documentElement.getAttribute('lang') || '').toLowerCase().slice(0, 2);
    return l || 'en';
  }

  /* 2026-09-15 Ales: show the reviewer as "Maja B." rather than the full name.
     Judge.me's own "Reviewer name format" setting is paid-plan only (checked in the admin), so the
     shortening is done here. It is presentation only - nothing about the stored review changes,
     and Judge.me still holds the real name for the shop owner.
     Rules: keep the first name, reduce every following name to an initial with a full stop.
     A single-word name is left exactly as it is. */
  function shortenName(el) {
    if (!el || el.dataset.s2gName === '1') return;
    var full = (el.textContent || '').trim().replace(/\s+/g, ' ');
    if (!full) return;
    var parts = full.split(' ');
    if (parts.length > 1) {
      var rest = parts.slice(1)
        .filter(Boolean)
        .map(function (w) { return w.charAt(0).toUpperCase() + '.'; })
        .join(' ');
      el.textContent = parts[0] + ' ' + rest;
    }
    el.dataset.s2gName = '1';
  }

  function apply(root) {
    var lang = pageLang();
    var revs = root.querySelectorAll('.jdgm-rev');
    for (var i = 0; i < revs.length; i++) {
      var r = revs[i];
      if (r.dataset.s2gLangDone === '1') continue;
      var body = r.querySelector('.jdgm-rev__body');
      var title = r.querySelector('.jdgm-rev__title');
      var text = ((title ? title.textContent : '') + ' ' + (body ? body.textContent : '')).trim();
      var got = detect(text);
      shortenName(r.querySelector('.jdgm-rev__author'));
      r.dataset.s2gLangDone = '1';
      if (got) {
        r.dataset.s2gLang = got;
        if (got !== lang) r.style.display = 'none';
      }
    }
  }

  function boot() {
    var wrap = document.querySelector('.s2g-revs');
    if (!wrap) return;
    apply(wrap);
    /* Judge.me renders late and re-renders on paging, so keep watching the container. */
    var mo = new MutationObserver(function () { apply(wrap); });
    mo.observe(wrap, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
